import { toast as sonnerToast, ExternalToast } from "sonner";

// Terminal-themed toast utilities
export const terminalToast = Object.assign(
  // Default toast function
  (message: string, options?: ExternalToast) => {
    return sonnerToast(message, {
      duration: 3500,
      ...options,
    });
  },
  {
    success: (message: string, options?: ExternalToast) => {
      return sonnerToast.success(message, {
        duration: 4000,
        ...options,
      });
    },

    error: (message: string, options?: ExternalToast) => {
      return sonnerToast.error(message, {
        duration: 5000,
        ...options,
      });
    },

    warning: (message: string, options?: ExternalToast) => {
      return sonnerToast.warning(message, {
        duration: 4500,
        ...options,
      });
    },

    info: (message: string, options?: ExternalToast) => {
      return sonnerToast.info(message, {
        duration: 3500,
        ...options,
      });
    },

    loading: (message: string, options?: ExternalToast) => {
      return sonnerToast.loading(message, {
        duration: Infinity,
        ...options,
      });
    },

    // Terminal command-style toasts
    command: (command: string, result: string, success: boolean = true) => {
      const message = `$ ${command}\n${result}`;
      return success ? terminalToast.success(message) : terminalToast.error(message);
    },

    // VI mode specific toasts
    vim: {
      modeChange: (mode: string) => {
        return terminalToast.info(`-- ${mode.toUpperCase()} --`, { duration: 2000 });
      },

      commandExecuted: (command: string) => {
        return terminalToast.success(`Command executed: ${command}`, { duration: 3000 });
      },

      invalidCommand: (command: string) => {
        return terminalToast.error(`Unknown command: ${command}`, { duration: 4000 });
      },
    },

    // Theme change notifications
    theme: {
      switched: (themeName: string) => {
        return terminalToast.success(`Theme switched to ${themeName} mode`, { duration: 3000 });
      },
    },

    // Navigation toasts
    nav: {
      navigated: (page: string) => {
        return terminalToast.success(`Navigated to ${page}`, { duration: 2500 });
      },
    },

    // System toasts
    system: {
      reload: () => {
        return terminalToast.info("Reloading application...", { duration: 2000 });
      },

      clearStorage: () => {
        return terminalToast.success("Storage cleared successfully", { duration: 3000 });
      },

      closeAttempt: () => {
        return terminalToast.warning("Close attempt blocked by browser", { duration: 4000 });
      },
    },

    // Fun/easter egg toasts
    fun: {
      cowsay: (message: string) => {
        return terminalToast(`[COW] ${message}`, { duration: 4000 });
      },

      fortune: (fortune: string) => {
        return terminalToast(`[FORTUNE] ${fortune}`, { duration: 5000 });
      },

      whoami: () => {
        return terminalToast("jonmatum@terminal - Full-stack engineer from Costa Rica", {
          duration: 4000,
        });
      },

      date: () => {
        return terminalToast(`[DATE] ${new Date().toLocaleString()}`, { duration: 3000 });
      },

      version: () => {
        return terminalToast("[VERSION] Jonmatum's Terminal v1.18.0 - VI mode enhanced", {
          duration: 4000,
        });
      },
    },
  }
);

// Re-export the original toast for backward compatibility
export const toast = sonnerToast;

// Default export for convenience
export default terminalToast;
